import { Fragment, useCallback, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import {
  faChevronRight,
  faCircleChevronDown,
  faCircleChevronRight,
} from '@fortawesome/pro-solid-svg-icons'
import {
  type Cell,
  type CellContext,
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  type Header,
  type HeaderContext,
  useReactTable,
} from '@tanstack/react-table'
import { clsx } from 'clsx'
import { isEmpty } from 'lodash-es'

import { TABLE_ROW } from 'Components/_theme/Table/Table.constants'
import { Icon } from 'Components/Icon/Icon'
import { NoResults } from 'Components/NoResults/NoResults'
import { NoResultsTypeEnum } from 'Components/NoResults/NoResults.types'
import { SearchLoadingState } from 'Components/SearchLoadingState/SearchLoadingState'

import { E2E_TABLE_ROW } from 'Constants/e2e'

import { SortableHeading } from 'Algolia/components/SortableHeading/SortableHeading'

import { Heading } from '../Heading/Heading'
import { TableCheckbox } from '../TableCheckbox/TableCheckbox'
import { TableListStaticColumnsEnum, type TableProps } from './Table.types'

/* eslint-disable react/destructuring-assignment -- selection props use a discriminated union that loses narrowing when destructured */
export const Table = <
  T extends {
    id?: string
    url?: string
    children?: T[]
  },
>(
  props: TableProps<T>,
) => {
  const {
    className,
    columns,
    data,
    isSearching,
    isLoading,
    onSortingChange,
    hideArrow,
    sorting,
    e2eRowId,
    previewedRowIndex,
    customNoResultsMessage,
    withSubrows,
  } = props

  const rowsRef = useRef<Map<number, HTMLTableRowElement | null>>(new Map())
  const intl = useIntl()

  const createHeaderClassNames = useCallback(
    (column: Header<T, unknown>) => {
      const baseClass = 'Table-cell Table-cell--head'

      if (column.id === TableListStaticColumnsEnum.SELECTION) {
        return clsx(baseClass, 'Table-cell--selection')
      }

      if (column.id === TableListStaticColumnsEnum.DETAILS) {
        return clsx(baseClass, 'Table-cell--details', {
          'Table-cell--details--withOptionalSubrows': withSubrows,
        })
      }

      if (column.id === TableListStaticColumnsEnum.ICON) {
        return clsx(baseClass, 'Table-cell--icon')
      }

      return baseClass
    },
    [withSubrows],
  )

  const createCellClassNames = useCallback(
    (cell: Cell<T, unknown>) => {
      const baseClass = 'Table-cell'

      if (cell.column.id === TableListStaticColumnsEnum.SELECTION) {
        return clsx(baseClass, 'Table-cell--selection')
      }

      if (cell.column.id === TableListStaticColumnsEnum.DETAILS) {
        return clsx(baseClass, 'Table-cell--details', {
          'Table-cell--details--withOptionalSubrows': withSubrows,
        })
      }

      if (cell.column.id === TableListStaticColumnsEnum.ICON) {
        return clsx(baseClass, 'Table-cell--icon')
      }

      return baseClass
    },
    [withSubrows],
  )

  const createDataE2E = useCallback(
    (cell: Cell<T, unknown>) => ({
      ...(cell.column.id === TableListStaticColumnsEnum.ICON && {
        'data-e2e': e2eRowId,
      }),
    }),
    [e2eRowId],
  )

  const handleExpandRow = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, clickedRowIndex: number) => {
      const { current: rows } = rowsRef

      if (!rows) {
        return
      }

      rows.forEach((row, rowIndex) => {
        if (rowIndex !== clickedRowIndex) {
          row?.classList.remove('Table-row--isExpanded')
        }
      })

      rows.get(clickedRowIndex)?.classList.toggle('Table-row--isExpanded')
    },
    [],
  )

  const renderDetailsButton = useCallback(
    ({ cell }: CellContext<T, unknown>) => (
      <button
        aria-label={intl.formatMessage({ id: 'Expand_content' })}
        className="Table-cell--details-button"
        onClick={event => handleExpandRow(event, cell.row.index)}
        type="button"
      >
        <Icon icon={faCircleChevronDown} size={20} />
      </button>
    ),
    [handleExpandRow, intl],
  )

  const renderExpandSubrowsButton = useCallback(
    ({ cell }: CellContext<T, unknown>) => {
      const isVisible = cell.row.getCanExpand()

      if (!isVisible) {
        return null
      }

      return (
        <button
          aria-label={intl.formatMessage({ id: 'Expand_content' })}
          className={clsx('Table-cell--details-button', {
            'Table-cell--details-button--subrowsCollapsed':
              !cell.row.getIsExpanded(),
            'Table-cell--details-button--subrowsExpanded':
              cell.row.getIsExpanded(),
          })}
          onClick={cell.row.getToggleExpandedHandler()}
          type="button"
        >
          <Icon icon={faCircleChevronRight} size={20} />
        </button>
      )
    },
    [intl],
  )

  const renderIcon = useCallback(
    ({ cell }: CellContext<T, unknown>) => {
      const { url } = cell.row.original

      if (!url) {
        return null
      }

      return (
        <Link
          aria-label={intl.formatMessage({ id: 'PreviewColumn_footer_button' })}
          title={intl.formatMessage({ id: 'PreviewColumn_footer_button' })}
          to={url}
        >
          <div className="Table-chevron d-flex justify-content-end">
            <Icon icon={faChevronRight} size={16} />
          </div>
        </Link>
      )
    },
    [intl],
  )

  const renderSelectionCell = useCallback(
    ({ row }: CellContext<T, unknown>) => (
      <TableCheckbox
        aria-label={intl.formatMessage({ id: 'Table_select_row' })}
        checked={row.getIsSelected()}
        onChange={row.getToggleSelectedHandler()}
      />
    ),
    [intl],
  )

  const renderSelectionHeader = useCallback(
    ({ table: headerTable }: HeaderContext<T, unknown>) => (
      <TableCheckbox
        aria-label={intl.formatMessage({ id: 'Table_select_all_rows' })}
        checked={headerTable.getIsAllPageRowsSelected()}
        indeterminate={headerTable.getIsSomePageRowsSelected()}
        onChange={headerTable.getToggleAllPageRowsSelectedHandler()}
      />
    ),
    [intl],
  )

  const tableColumns = useMemo<ColumnDef<T>[]>(() => {
    const detailsColumn: ColumnDef<T> = {
      cell: withSubrows ? renderExpandSubrowsButton : renderDetailsButton,
      id: TableListStaticColumnsEnum.DETAILS,
    }

    const iconColumn: ColumnDef<T> = {
      cell: renderIcon,
      id: TableListStaticColumnsEnum.ICON,
    }

    const preparedColumns = columns.map(column => ({
      ...column,
    }))

    const result = hideArrow
      ? [detailsColumn, ...preparedColumns]
      : [detailsColumn, ...preparedColumns, iconColumn]

    if (props.enableRowSelection) {
      const selectionColumn: ColumnDef<T> = {
        cell: renderSelectionCell,
        header: renderSelectionHeader,
        id: TableListStaticColumnsEnum.SELECTION,
      }

      return [selectionColumn, ...result]
    }

    return result
  }, [
    columns,
    hideArrow,
    props.enableRowSelection,
    renderDetailsButton,
    renderExpandSubrowsButton,
    renderIcon,
    renderSelectionCell,
    renderSelectionHeader,
    withSubrows,
  ])

  const table = useReactTable({
    columns: tableColumns,
    data,
    enableRowSelection: !!props.enableRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: row =>
      Boolean(row.original.children && row.original.children?.length > 0),
    ...(props.enableRowSelection && {
      getRowId: (row: T) => {
        if (!row.id) {
          throw new Error(
            'All rows must have a unique "id" property when row selection is enabled.',
          )
        }

        return row.id
      },
    }),
    getSubRows: row => row.children,
    manualSorting: true,
    onRowSelectionChange: props.enableRowSelection
      ? props.onRowSelectionChange
      : undefined,
    onSortingChange,
    state: {
      sorting,
      ...(props.enableRowSelection && {
        rowSelection: props.rowSelection,
      }),
    },
  })
  const { rows } = table.getRowModel()

  const renderProgressInfo = useMemo(() => {
    let progressInfo = null

    if (isSearching || isLoading) {
      progressInfo = <SearchLoadingState />
    }

    if (!isLoading && isEmpty(data)) {
      progressInfo = (
        <NoResults
          className="pb-4"
          noResultsText={customNoResultsMessage}
          type={
            customNoResultsMessage
              ? NoResultsTypeEnum.custom
              : NoResultsTypeEnum.simple
          }
        />
      )
    }

    if (!progressInfo) {
      return null
    }

    return (
      <tr className="Table-row Table-row--no-results" role="row">
        <td className="Table-cell" colSpan={table.getAllColumns().length}>
          {progressInfo}
        </td>
      </tr>
    )
  }, [isSearching, isLoading, data, customNoResultsMessage, table])

  const renderExpandableContent = useCallback((cells: Cell<T, unknown>[]) => {
    const expandableContentIndex = cells.findIndex(
      cell =>
        typeof cell.column.columnDef.meta?.ExpandableContent === 'function',
    )

    if (expandableContentIndex === -1) {
      return null
    }

    return (
      <tr className="Table-row Table-row--expandable" role="row">
        <td className="Table-row--expandable-cell w-100">
          {flexRender(
            cells[expandableContentIndex].column.columnDef.meta
              ?.ExpandableContent,
            cells[expandableContentIndex].getContext(),
          )}
        </td>
      </tr>
    )
  }, [])

  const renderHeaderContent = useCallback((header: Header<T, unknown>) => {
    const isStaticColumn =
      header.id === TableListStaticColumnsEnum.SELECTION ||
      header.id === TableListStaticColumnsEnum.DETAILS ||
      header.id === TableListStaticColumnsEnum.ICON

    if (isStaticColumn) {
      return flexRender(header.column.columnDef.header, header.getContext())
    }

    if (header.column.getCanSort()) {
      return (
        <SortableHeading
          as={6}
          clearSorting={header.column.clearSorting}
          columnName={header.column.columnDef.id!}
          getIsSorted={header.column.getIsSorted}
          size={2}
          toggleSorting={header.column.toggleSorting}
        >
          {flexRender(header.column.columnDef.header, header.getContext())}
        </SortableHeading>
      )
    }

    return (
      <Heading as={6} size={2}>
        {flexRender(header.column.columnDef.header, header.getContext())}
      </Heading>
    )
  }, [])

  const rowSelection = props.enableRowSelection ? props.rowSelection : undefined

  const renderRows = rows.map(row => {
    const cells = row.getVisibleCells()
    const isSelected = rowSelection ? row.getIsSelected() : false

    return (
      <Fragment key={`row__${row.id}`}>
        <tr
          ref={tableRow => {
            rowsRef.current.set(row.index, tableRow)
          }}
          aria-selected={isSelected || undefined}
          className={clsx('Table-row', {
            'Table-row--isSelected': withSubrows
              ? previewedRowIndex === row.id
              : previewedRowIndex === row.index,
            'Table-row--isSubrow': row.depth > 0,
            'Table-row--withExpandedSubrows': row.getIsExpanded(),
            'Table-row--withOptionalSubrows': withSubrows,
          })}
          data-e2e={E2E_TABLE_ROW}
          data-id={row.original.id}
          data-testid={TABLE_ROW}
          role="row"
        >
          {cells.map(cell => (
            <td
              key={cell.id}
              className={clsx(createCellClassNames(cell))}
              {...createDataE2E(cell)}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </td>
          ))}
        </tr>

        {renderExpandableContent(cells)}
      </Fragment>
    )
  })

  return (
    <table className={clsx('Table', className)} role="table">
      <thead className="Table-head">
        {table.getHeaderGroups().map(headerGroup => (
          <tr
            key={headerGroup.id}
            className="Table-row Table-row--head"
            role="row"
          >
            {headerGroup.headers.map(header => (
              <th
                key={header.id}
                className={clsx(createHeaderClassNames(header))}
              >
                {renderHeaderContent(header)}
              </th>
            ))}
          </tr>
        ))}
      </thead>

      <tbody className="Table-body">
        {renderRows}

        {renderProgressInfo}
      </tbody>
    </table>
  )
}
